import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, ExternalLink, Loader2, Lock, Send, X, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { QK_POORTWACHTER } from './PoortwachterSigningWrapper';
import { downloadUwvBewijs, getUwvVoorbereiding, verzendUwvMelding, type PoortwachterCase, type UwvKanaal, type UwvMelding, type UwvMeldingSoort } from '../services/lopendeTrajectenApi';

/**
 * Stap 5 — Verzend digitale UWV-melding.
 *
 * Twee kanalen: 'koppeling' (Belagom stuurt de HMAC-ondertekende melding naar
 * de HR-/Digipoort-connector van de werkgever) of 'portaal' (HR meldt in het
 * UWV-werkgeversportaal; Belagom legt tijdstip, gebruiker, IP en referentie
 * vast en genereert een verzendbewijs). Beide ronden de mijlpaal af.
 * De melding bevat geen naam of BSN.
 */

const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');
const fmtDateTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

const StatusBadge: React.FC<{ m: UwvMelding }> = ({ m }) => {
  const { t } = useTranslation('employer');
  const tone = m.status === 'MISLUKT' ? 'bg-red-100 text-red-800' : m.status === 'BEVESTIGD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${tone}`}>{m.status === 'MISLUKT' ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />} {t(`uwvMelding.status.${m.status}`)}</span>;
};

export const UwvMeldingModal: React.FC<{ c: PoortwachterCase; soort?: UwvMeldingSoort; onSluit: () => void }> = ({ c, soort = '42e_week', onSluit }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const { data: p, isLoading, error } = useQuery({ queryKey: [...QK_POORTWACHTER, 'uwv', c.id, soort], queryFn: () => getUwvVoorbereiding(c.id, soort) });
  const [kanaal, setKanaal] = useState<UwvKanaal>('portaal');
  const [lh, setLh] = useState('');
  const [referentie, setReferentie] = useState('');
  const [bevestigd, setBevestigd] = useState(false);
  const [downloadt, setDownloadt] = useState<string | null>(null);
  useEffect(() => {
    if (p) {
      setKanaal(p.kanalen.koppeling.beschikbaar ? 'koppeling' : 'portaal');
      setLh(p.werkgever.loonheffingennummer ?? '');
    }
  }, [p]);

  const m = useMutation({
    mutationFn: () => verzendUwvMelding(c.id, { soort, kanaal, uwv_referentie: referentie || null, loonheffingennummer: lh || null, bevestigd }),
    onSuccess: (r) => {
      toast.success(
        r.status === 'BEVESTIGD'
          ? r.uwv_referentie
            ? t('uwvMelding.toastBevestigdRef', { ref: r.uwv_referentie })
            : t('uwvMelding.toastBevestigd')
          : t('uwvMelding.toastGeregistreerd')
      );
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
    },
    onError: (e: any) => {
      toast.error(apiMessage(e, t('uwvMelding.foutVerzenden')));
      qc.invalidateQueries({ queryKey: [...QK_POORTWACHTER, 'uwv', c.id, soort] });
    },
  });
  const bewijs = async (melding: UwvMelding) => {
    setDownloadt(melding.id);
    try {
      await downloadUwvBewijs(c.id, melding, c.case_code);
    } catch (e: any) {
      toast.error(apiMessage(e, t('uwvMelding.foutBewijs')));
    } finally {
      setDownloadt(null);
    }
  };
  const lhGeldig = /^\d{9}L\d{2}$/.test(lh.trim().toUpperCase());
  const kanVerzenden = lhGeldig && (kanaal === 'koppeling' || bevestigd) && !m.isPending;
  const afgerond = p?.mijlpaal?.status === 'AFGEROND';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label={t('uwvMelding.ariaLabel')}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Send className="w-4 h-4 text-red-600" /> {t('uwvMelding.titel', { caseCode: c.case_code })}</h3>
          <button type="button" onClick={onSluit} aria-label={t('uwvMelding.sluiten')} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {isLoading || !p ? (
          <div className="p-10 flex justify-center">{error ? <span className="text-sm text-red-700">{apiMessage(error, t('uwvMelding.foutLaden'))}</span> : <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-sm font-semibold text-gray-900">{p.soort_label}</div>
            {p.mijlpaal && (
              <div className={`p-2 rounded-lg text-xs border ${afgerond ? 'bg-green-50 border-green-200 text-green-900' : p.mijlpaal.dagen_te_gaan < 0 ? 'bg-red-50 border-red-300 text-red-900' : 'bg-yellow-50 border-yellow-200 text-yellow-900'}`}>
                {afgerond
                  ? t('uwvMelding.deadlineAfgerond', { datum: fmtDate(p.mijlpaal.deadline) })
                  : p.mijlpaal.dagen_te_gaan < 0
                    ? t('uwvMelding.deadlineOvertijd', { datum: fmtDate(p.mijlpaal.deadline), dagen: -p.mijlpaal.dagen_te_gaan })
                    : t('uwvMelding.deadlineTeGaan', { datum: fmtDate(p.mijlpaal.deadline), dagen: p.mijlpaal.dagen_te_gaan })}
              </div>
            )}

            {/* Vooringevulde melding (zonder PII) */}
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs">
              {[
                [t('uwvMelding.veldWerkgever'), p.werkgever.bedrijfsnaam],
                [t('uwvMelding.veldKvk'), p.werkgever.kvk_nummer],
                [t('uwvMelding.veldVestiging'), p.case.vestiging],
                [t('uwvMelding.veldWerknemer'), `${p.case.pseudoniem ?? '—'} (${p.case.case_code})`],
                [t('uwvMelding.veldEersteZiektedag'), fmtDate(p.case.eerste_ziektedag)],
                [t('uwvMelding.veldVerzuimweek'), t('uwvMelding.verzuimweekWaarde', { week: p.case.week_nu })],
              ].map(([k, v]) => (
                <div key={k as string}><dt className="text-gray-500">{k}</dt><dd className="font-medium text-gray-900">{v || '—'}</dd></div>
              ))}
            </dl>
            <p className="text-[11px] text-gray-600 flex items-start gap-1"><Lock className="w-3 h-3 mt-0.5 flex-shrink-0" /> {p.privacy_notitie}</p>

            <div className="w-64">
              <label className={labelClass} htmlFor="uwv-lh">{t('uwvMelding.lhLabel')}</label>
              <input id="uwv-lh" className={inputClass} placeholder="123456789L01" value={lh} maxLength={12} onChange={(e) => setLh(e.target.value.toUpperCase())} />
              {!lhGeldig && lh && <p className="text-[11px] text-red-700 mt-1">{t('uwvMelding.lhFormaat')}</p>}
              {!p.werkgever.loonheffingennummer && <p className="text-[11px] text-gray-500 mt-1">{t('uwvMelding.lhBewaard')}</p>}
            </div>

            {/* Kanaal */}
            <fieldset className="space-y-2">
              <legend className={labelClass}>{t('uwvMelding.kanaalLegend')}</legend>
              {/* Kanaal 'koppeling' alleen als deze werkgever een connector heeft (eigen of platformterugval) */}
              {p.kanalen.koppeling.beschikbaar && (
                <label className={`flex items-start gap-2 p-3 rounded-lg border text-sm cursor-pointer ${kanaal === 'koppeling' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input type="radio" name="kanaal" className="mt-1" checked={kanaal === 'koppeling'} onChange={() => setKanaal('koppeling')} />
                  <span><strong>{t('uwvMelding.koppelingTitel')}</strong> {t('uwvMelding.koppelingSub')}<br /><span className="text-xs text-gray-600">{p.kanalen.koppeling.omschrijving}</span></span>
                </label>
              )}
              <label className={`flex items-start gap-2 p-3 rounded-lg border text-sm cursor-pointer ${kanaal === 'portaal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <input type="radio" name="kanaal" className="mt-1" checked={kanaal === 'portaal'} onChange={() => setKanaal('portaal')} />
                <span><strong>{t('uwvMelding.portaalTitel')}</strong><br /><span className="text-xs text-gray-600">{t('uwvMelding.portaalUitleg')}</span></span>
              </label>
            </fieldset>

            {kanaal === 'portaal' && (
              <div className="space-y-2 p-3 rounded-lg border border-red-200 bg-red-50">
                <a href={p.kanalen.portaal.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> {t('uwvMelding.portaalOpenen')}
                </a>
                <div className="w-72">
                  <label className={labelClass} htmlFor="uwv-ref">{t('uwvMelding.refLabel')}</label>
                  <input id="uwv-ref" className={inputClass} value={referentie} maxLength={80} onChange={(e) => setReferentie(e.target.value)} placeholder={t('uwvMelding.refPlaceholder')} />
                </div>
                <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={bevestigd} onChange={(e) => setBevestigd(e.target.checked)} />
                  <span>{t('uwvMelding.bevestigPortaal', { soort: p.soort_label.toLowerCase(), wie: p.case.pseudoniem ?? p.case.case_code })}</span>
                </label>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-3 border-t">
              <p className="text-xs text-gray-500">{t('uwvMelding.naVerzending')}</p>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={onSluit}>{t('uwvMelding.sluiten')}</Button>
                <Button size="sm" disabled={!kanVerzenden} onClick={() => m.mutate()}>
                  {m.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> {kanaal === 'koppeling' ? t('uwvMelding.verzendKoppeling') : t('uwvMelding.registreer')}</>}
                </Button>
              </div>
            </div>

            {p.historie.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">{t('uwvMelding.verzendlog')}</h4>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-xs">
                  {p.historie.map((h) => (
                    <li key={h.id} className="flex items-center justify-between gap-2 p-2">
                      <div>
                        <div className="flex items-center gap-2"><StatusBadge m={h} /> <span className="font-medium">{h.soort_label}</span> <span className="text-gray-500">{t('uwvMelding.via', { kanaal: t(`uwvMelding.kanaal.${h.kanaal}`) })}</span></div>
                        <div className="text-gray-500">{fmtDateTime(h.verzonden_op)}{h.uwv_referentie ? ` · ${t('uwvMelding.ref', { ref: h.uwv_referentie })}` : ''}{h.foutmelding ? ` · ${h.foutmelding}` : ''}</div>
                      </div>
                      <button type="button" onClick={() => bewijs(h)} disabled={downloadt === h.id} className="inline-flex items-center gap-1 text-blue-700 hover:underline whitespace-nowrap">
                        {downloadt === h.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {t('uwvMelding.bewijsPdf')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UwvMeldingModal;
